import { useToast } from '@mdvip/shared/hooks/use-toast';
import { useErrorTranslation } from '@mdvip/shared/hooks/useErrorTranslation';
import { extractGqlErrorCode } from '@mdvip/shared/lib/gqlErrors';
import { trackException } from '@mdvip/shared/lib/telemetry';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { Gender, PreferredContactMethod, UpdateMyMemberInfoInput } from '@/graphql/generated/graphql';
import { type MyMemberInfo, memberInfoKey } from '@/hooks/member/memberInfoApi';
import { stripPhoneFormatting } from '@/lib/profile/profile.format';
import type { OriginalAddressLines, ProfileFormValues, ProfileValidationIssue } from '@/lib/profile/profile.types';
import { requestUpdateMyMemberInfo, UpdateMyMemberInfoError } from './profileApi';

/**
 * Resolves a single optional field to the backend's three-state contract: a value overwrites,
 * `null` clears, and `undefined` (omitted from the payload entirely) leaves it unchanged. A blank
 * form field is never sent as `''` — it resolves to `null` only if the field held a value when the
 * dialog opened (an explicit clear), otherwise `undefined` (never touched).
 */
function optionalField(current: string, initial: string): string | null | undefined {
  const trimmed = current.trim();
  if (trimmed !== '') return trimmed;
  return initial.trim() !== '' ? null : undefined;
}

/**
 * Resolves the mailing address's `mailingLine1`/`mailingLine2` from the form's one flattened
 * field. Both are always sent — `mailingLine1` is required, so every save resends it, and
 * `updateMyMemberInfo` recomputes the whole street from whichever of the two lines it's given
 * (there's no way to update one line without resending the other — see `OriginalAddressLines`),
 * so an untouched `mailingLine2` must always travel alongside it, never be silently omitted.
 */
function resolveMailingStreetLines(
  values: ProfileFormValues,
  initial: ProfileFormValues,
  original: OriginalAddressLines,
): Pick<UpdateMyMemberInfoInput, 'mailingLine1' | 'mailingLine2'> {
  const touched = values.mailingStreet.trim() !== initial.mailingStreet.trim();
  if (touched) {
    // The flattened field can't hold a second line, so editing it means "the address is now this
    // one line" — any real line2 is gone.
    return { mailingLine1: values.mailingStreet.trim(), mailingLine2: null };
  }
  // Untouched: forward the real pair verbatim. The `mailingStreet` fallback for `mailingLine1` is
  // just a type-safety net — it's required, so a real value always exists here.
  return { mailingLine1: original.mailingLine1 ?? values.mailingStreet.trim(), mailingLine2: original.mailingLine2 };
}

type OtherAddressFields = Pick<
  UpdateMyMemberInfoInput,
  'otherLine1' | 'otherLine2' | 'otherCity' | 'otherState' | 'otherPostalCode'
>;

/**
 * Resolves the secondary address's four fields as one unit, not field-by-field — the backend
 * requires `otherLine1`/`otherCity`/`otherState`/`otherPostalCode` to be omitted, set, or nulled
 * together, never a mix (`update-my-member-info.model.ts`'s cross-field `refine`), and sparse or
 * narrowed on-file data (see `OriginalAddressLines`) means resolving each field independently
 * can't guarantee that.
 *
 * Untouched group: omit every field — on-file data may already be inconsistent, so no resend is
 * safe. Touched group: `profile.schema.ts`'s all-or-nothing check has already guaranteed it's now
 * fully blank or fully filled, so `otherCity`/`otherState`/`otherPostalCode` read straight from
 * `values`. `otherLine1`/`otherLine2` still fall back to `original` when a sibling (not the
 * street) triggered the resend — the flattened `otherStreet` display can't be split back into two
 * real lines.
 */
function resolveOtherAddress(
  values: ProfileFormValues,
  initial: ProfileFormValues,
  original: OriginalAddressLines,
): OtherAddressFields {
  const streetTouched = values.otherStreet.trim() !== initial.otherStreet.trim();
  const groupTouched =
    streetTouched ||
    values.otherCity.trim() !== initial.otherCity.trim() ||
    values.otherState.trim() !== initial.otherState.trim() ||
    values.otherPostalCode.trim() !== initial.otherPostalCode.trim();

  if (!groupTouched) {
    return {
      otherLine1: undefined,
      otherLine2: undefined,
      otherCity: undefined,
      otherState: undefined,
      otherPostalCode: undefined,
    };
  }

  const isBlank =
    values.otherStreet.trim() === '' &&
    values.otherCity.trim() === '' &&
    values.otherState.trim() === '' &&
    values.otherPostalCode.trim() === '';

  if (isBlank) {
    return { otherLine1: null, otherLine2: null, otherCity: null, otherState: null, otherPostalCode: null };
  }

  return {
    // Unlike `mailingLine1`, `otherLine1` is optional — `original.otherLine1` can legitimately be
    // `null` (a sparse on-file record with no street), so it's used directly here, no `values`
    // fallback: falling back to the blank flattened field would send `''` instead of `null`.
    otherLine1: streetTouched ? values.otherStreet.trim() : original.otherLine1,
    otherLine2: streetTouched ? null : original.otherLine2,
    otherCity: values.otherCity.trim(),
    otherState: values.otherState.trim(),
    otherPostalCode: values.otherPostalCode.trim(),
  };
}

/**
 * Builds the `updateMyMemberInfo` payload from the form's current vs. initial values. `initial` is
 * the dialog's original seed, not a fresh read, so toggling a field back to its original value
 * still resolves as unchanged (omitted).
 *
 * `originalAddressLines` defaults to the flattened form values (no real `line2`/`otherState`) — a
 * caller without the real Salesforce shape just gets today's narrowed behavior back.
 */
export function buildUpdatePayload(
  values: ProfileFormValues,
  initial: ProfileFormValues,
  originalAddressLines: OriginalAddressLines = {
    mailingLine1: initial.mailingStreet.trim(),
    mailingLine2: null,
    otherLine1: initial.otherStreet.trim() === '' ? null : initial.otherStreet.trim(),
    otherLine2: null,
  },
): UpdateMyMemberInfoInput {
  return {
    dateOfBirth: values.dateOfBirth,
    // profileSchema's superRefine rejects '' before submit can be reached, so this is never ''.
    gender: values.gender as Gender,
    email: values.email.trim(),
    phone: stripPhoneFormatting(values.phone),
    ...resolveMailingStreetLines(values, initial, originalAddressLines),
    mailingCity: values.mailingCity.trim(),
    mailingState: values.mailingState.trim(),
    mailingPostalCode: values.mailingPostalCode.trim(),
    salutation: optionalField(values.salutation, initial.salutation),
    mobilePhone: optionalField(stripPhoneFormatting(values.mobilePhone), stripPhoneFormatting(initial.mobilePhone)),
    ...resolveOtherAddress(values, initial, originalAddressLines),
    primaryInsurance: optionalField(values.primaryInsurance, initial.primaryInsurance),
    secondaryInsurance: optionalField(values.secondaryInsurance, initial.secondaryInsurance),
    employer: optionalField(values.employer, initial.employer),
    jobTitle: optionalField(values.jobTitle, initial.jobTitle),
    preferredContactMethod: optionalField(values.preferredContactMethod, initial.preferredContactMethod) as
      | PreferredContactMethod
      | null
      | undefined,
  };
}

interface UseUpdateMyProfileOptions {
  /** Called after a successful save — the dialog decides toast/close, same contract as the payments Edit modals. */
  onSuccess?: () => void;
  /** `BAD_REQUEST`'s `issues[]` — the dialog owns the form, so it maps these to `setError`/a banner, not this hook. */
  onValidationError?: (issues: ProfileValidationIssue[]) => void;
}

/**
 * Saves the authenticated member's own profile. On success, merges the mutation's response
 * directly into the shared `myMemberInfo` cache entry (not a wholesale replace — the mutation's
 * selection is a deliberate subset, see `profileApi.ts`'s `UpdatedMemberInfo`) so `useMyProfile`
 * reflects the save with no refetch, while Payments' billing/AutoPay/balance views sharing the same
 * cache entry keep the fields this mutation never touches.
 */
export function useUpdateMyProfile({ onSuccess, onValidationError }: UseUpdateMyProfileOptions = {}) {
  const { toast } = useToast();
  const tError = useErrorTranslation();
  const queryClient = useQueryClient();

  const mutation = useMutation({
    mutationFn: (input: UpdateMyMemberInfoInput) => requestUpdateMyMemberInfo(input),
    onSuccess: (updatedMember) => {
      queryClient.setQueryData<MyMemberInfo>(memberInfoKey, (existing) =>
        existing ? { ...existing, ...updatedMember } : existing,
      );
      onSuccess?.();
    },
    onError: (error: unknown) => {
      if (error instanceof UpdateMyMemberInfoError && onValidationError) {
        onValidationError(error.issues);
        return;
      }
      // Infrastructure error, or a BAD_REQUEST with no caller wired up for field-level errors —
      // either way, fall back to a toast so it's never fully silent. `UpdateMyMemberInfoError`
      // isn't a `ClientError`, so read its own `.message` ('BAD_REQUEST') instead of falling
      // through `extractGqlErrorCode` to the less specific 'UNKNOWN'.
      const code = error instanceof UpdateMyMemberInfoError ? error.message : extractGqlErrorCode(error);
      // `trackException` forwards `.message` to Application Insights verbatim — a raw `ClientError`
      // embeds this mutation's own PHI-bearing input via graphql-request's `JSON.stringify`, so only
      // a sanitized error ever reaches telemetry.
      trackException(new Error(`updateMyMemberInfo failed: ${code}`), {
        source: 'updateMyMemberInfo',
        errorCode: code,
      });
      toast({ title: tError('title'), description: tError(code), variant: 'destructive' });
    },
  });

  return {
    submit: (values: ProfileFormValues, initial: ProfileFormValues, originalAddressLines?: OriginalAddressLines) =>
      mutation.mutate(buildUpdatePayload(values, initial, originalAddressLines)),
    isPending: mutation.isPending,
  };
}
